// Theme Configuration
// Professional dashboard design inspired by modern admin panels
export const themeConfig = {
  // Color Palettes
  colors: {
    // Professional primary colors (purple-blue theme)
    primary: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#6366f1", // Professional purple-blue
      600: "#4f46e5",
      700: "#4338ca",
      800: "#3730a3",
      900: "#312e81",
    },

    // Clean background colors (matching screenshot)
    background: {
      primary: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", // Main background
      secondary: "#f8fafc", // Clean white/light gray
      card: "#ffffff", // Pure white cards
      sidebar: "rgba(255, 255, 255, 0.95)", // Clean sidebar
      dashboard:
        "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #a855f7 50%, #8b5cf6 75%, #7c3aed 100%)",
    },

    // Neutral colors
    gray: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },

    // Semantic colors
    success: {
      light: "#dcfce7",
      main: "#16a34a",
      dark: "#15803d",
    },
    warning: {
      light: "#fef3c7",
      main: "#f59e0b",
      dark: "#d97706",
    },
    error: {
      light: "#fee2e2",
      main: "#dc2626",
      dark: "#b91c1c",
    },
    info: {
      light: "#dbeafe",
      main: "#2563eb",
      dark: "#1d4ed8",
    },

    // Card colors (inspired by screenshot)
    cards: {
      blue: {
        light: "#e0f2fe",
        main: "#0ea5e9",
        dark: "#0284c7",
        gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
      },
      green: {
        light: "#dcfce7",
        main: "#16a34a",
        dark: "#15803d",
        gradient: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
      },
      orange: {
        light: "#fed7aa",
        main: "#f97316",
        dark: "#ea580c",
        gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      },
      purple: {
        light: "#e9d5ff",
        main: "#a855f7",
        dark: "#9333ea",
        gradient: "linear-gradient(135deg, #a855f7 0%, #9333ea 100%)",
      },
    },
  },

  // Typography
  typography: {
    fontFamily: {
      primary: ["Inter", "system-ui", "sans-serif"],
      secondary: ["Poppins", "system-ui", "sans-serif"],
      mono: ["JetBrains Mono", "Monaco", "Consolas", "monospace"],
    },
    fontSize: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      base: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.25rem", // 20px
      "2xl": "1.5rem", // 24px
      "3xl": "1.875rem", // 30px
      "4xl": "2.25rem", // 36px
      "5xl": "3rem", // 48px
    },
    fontWeight: {
      light: "300",
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
      extrabold: "800",
    },
  },

  // Spacing
  spacing: {
    xs: "0.25rem", // 4px
    sm: "0.5rem", // 8px
    md: "1rem", // 16px
    lg: "1.5rem", // 24px
    xl: "2rem", // 32px
    "2xl": "3rem", // 48px
    "3xl": "4rem", // 64px
  },

  // Border radius
  borderRadius: {
    none: "0",
    sm: "0.125rem", // 2px
    md: "0.375rem", // 6px
    lg: "0.5rem", // 8px
    xl: "0.75rem", // 12px
    "2xl": "1rem", // 16px
    "3xl": "1.5rem", // 24px
    full: "9999px",
  },

  // Shadows
  boxShadow: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    card: "0 4px 20px rgba(0, 0, 0, 0.08)",
    elegant: "0 8px 30px rgba(0, 0, 0, 0.12)",
  },

  // Animation
  animation: {
    duration: {
      fast: "200ms",
      normal: "300ms",
      slow: "500ms",
    },
    easing: {
      default: "cubic-bezier(0.4, 0, 0.2, 1)",
      in: "cubic-bezier(0.4, 0, 1, 1)",
      out: "cubic-bezier(0, 0, 0.2, 1)",
      inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    },
  },

  // Component specific
  components: {
    sidebar: {
      width: "280px",
      background: "rgba(255, 255, 255, 0.95)",
      backdropBlur: "20px",
    },
    header: {
      height: "80px",
      background: "rgba(255, 255, 255, 0.95)",
      backdropBlur: "20px",
    },
    card: {
      background: "rgba(255, 255, 255, 0.95)",
      backdropBlur: "20px",
      borderRadius: "16px",
      padding: "24px",
    },
    button: {
      borderRadius: "12px",
      padding: "12px 24px",
    },
  },
};

// Theme variants
export const themeVariants = {
  light: {
    background: themeConfig.colors.background.dashboard,
    surface: "#ffffff",
    text: {
      primary: "#1e293b",
      secondary: "#64748b",
      muted: "#94a3b8",
    },
  },
  dark: {
    background: "#0b1220",
    surface: "#1e293b",
    text: {
      primary: "#f1f5f9",
      secondary: "#cbd5e1",
      muted: "#94a3b8",
    },
  },
};

export default themeConfig;
